import { ReactNode, useEffect, useState, useTransition } from 'react';
import { Location, useLocation } from 'react-router-dom';
import { durations } from '../../styles/tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Curtain, View } from './styles';

type Phase = 'idle' | 'out' | 'in';

interface RouteTransitionProps {
  children: (location: Location) => ReactNode;
}

/*
 * Outgoing view fades while a --night-0 panel wipes in from the top; the swap happens under the
 * panel (a React transition, so a lazy route never blanks); the panel then fades away.
 * Instant under reduced motion. Hash-only changes swap immediately.
 */
export default function RouteTransition({ children }: RouteTransitionProps) {
  const location = useLocation();
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(location);
  const [phase, setPhase] = useState<Phase>('idle');
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (location === shown) return;
    if (location.pathname === shown.pathname || reduced) {
      setShown(location);
      return;
    }
    setPhase('out');
    const timer = window.setTimeout(() => {
      startTransition(() => setShown(location));
    }, durations.ui);
    return () => window.clearTimeout(timer);
  }, [location, shown, reduced]);

  useEffect(() => {
    if (phase !== 'out' || isPending || shown.pathname !== location.pathname) return;
    setPhase('in');
    const timer = window.setTimeout(() => setPhase('idle'), durations.ui);
    return () => window.clearTimeout(timer);
  }, [phase, isPending, shown, location]);

  /* Scroll for the page that is actually shown, not the one that is still leaving. */
  useEffect(() => {
    if (shown.hash) {
      const el = document.getElementById(shown.hash.slice(1));
      if (el) el.scrollIntoView({ block: 'start' });
      return;
    }
    window.scrollTo(0, 0);
  }, [shown]);

  return (
    <>
      <View $phase={phase}>{children(shown)}</View>
      <Curtain aria-hidden="true" $phase={phase} />
    </>
  );
}
